import { useMutation, useQuery } from '@tanstack/react-query';
import { queryContext } from "../context/fractal-sdk-context.js";
import { sdkApiClient, webSdkApiClient } from "../core/api/client.js";
import { ApiFeature } from "../core/api/types.js";
import { FractalSDKAuthenticationError, FractalSDKBuyItemUnknownError, } from "../core/error/index.js";
import { FractalSDKGetItemsUnknownError, FractalSDKListItemUnknownError, FractalSDKCancelListItemUnknownError, FractalSDKGetItemsForSaleUnknownError, } from "../core/error/item.js";
import { useUser } from "../hooks/public/use-user.js";
import { useUserWallet } from "../hooks/public/use-user-wallet.js";
import { isNotNullOrUndefined } from "../lib/guards/index.js";
var ItemApiKey;
(function (ItemApiKey) {
    ItemApiKey["GENERATE_BUY_TRANSACTION"] = "GENERATE_BUY_TRANSACTION";
    ItemApiKey["GENERATE_LIST_TRANSACTION"] = "GENERATE_LIST_TRANSACTION";
    ItemApiKey["GENERATE_CANCEL_LIST_TRANSACTION"] = "GENERATE_CANCEL_LIST_TRANSACTION";
    ItemApiKey["GET_ITEMS"] = "GET_ITEMS";
    ItemApiKey["GET_ITEMS_FOR_SALE"] = "GET_ITEMS_FOR_SALE";
})(ItemApiKey || (ItemApiKey = {}));
export const ItemApiKeys = {
    getItems: (userId) => [ApiFeature.ITEMS, ItemApiKey.GET_ITEMS, userId],
    getItemsForSale: ({ limit, sortDirection, sortField }, userId) => [
        ApiFeature.ITEMS,
        ItemApiKey.GET_ITEMS_FOR_SALE,
        limit,
        sortDirection,
        sortField,
        userId,
    ],
};
export const useGetItemsQuery = () => {
    const { data: user } = useUser();
    const query = useQuery(ItemApiKeys.getItems(user === null || user === void 0 ? void 0 : user.userId), async () => CoinApi.getItems(), {
        context: queryContext,
        enabled: isNotNullOrUndefined(user),
    });
    if (user === undefined) {
        query.remove();
    }
    return query;
};
export const useGetItemsForSaleQuery = (params) => {
    const { data: user } = useUser();
    const query = useQuery(ItemApiKeys.getItemsForSale(params, user === null || user === void 0 ? void 0 : user.userId), async () => CoinApi.getItemsForSale(params), {
        context: queryContext,
        enabled: isNotNullOrUndefined(user),
    });
    if (user === undefined) {
        query.remove();
    }
    return query;
};
export const useGenerateBuyTransactionMutation = () => {
    const { data: userWallet } = useUserWallet();
    return useMutation(async ({ quantity, tokenId, }) => {
        const walletId = userWallet === null || userWallet === void 0 ? void 0 : userWallet.solanaPublicKeys[0];
        if (!walletId) {
            throw new FractalSDKAuthenticationError('Missing wallet address.' +
                `Expected a non-empty string but received ${walletId}`);
        }
        return CoinApi.generateBuyTransaction({
            quantity,
            tokenId,
            walletId,
        });
    }, {
        context: queryContext,
    });
};
export const useGenerateListTransactionMutation = () => {
    const { data: userWallet } = useUserWallet();
    return useMutation(async ({ price, quantity, tokenId, }) => {
        const walletId = userWallet === null || userWallet === void 0 ? void 0 : userWallet.solanaPublicKeys[0];
        if (!walletId) {
            throw new FractalSDKAuthenticationError('Missing wallet address.' +
                `Expected a non-empty string but received ${walletId}`);
        }
        return CoinApi.generateListTransaction({
            price,
            quantity,
            tokenId,
            walletId,
        });
    }, {
        context: queryContext,
    });
};
export const useGenerateCancelListTransactionMutation = () => {
    const { data: userWallet } = useUserWallet();
    return useMutation(async ({ quantity, tokenId, }) => {
        const walletId = userWallet === null || userWallet === void 0 ? void 0 : userWallet.solanaPublicKeys[0];
        if (!walletId) {
            throw new FractalSDKAuthenticationError('Missing wallet address.' +
                `Expected a non-empty string but received ${walletId}`);
        }
        return CoinApi.generateCancelListTransaction({
            quantity,
            tokenId,
            walletId,
        });
    }, {
        context: queryContext,
    });
};
const CoinApi = {
    generateBuyTransaction,
    generateCancelListTransaction,
    generateListTransaction,
    getItems,
    getItemsForSale,
};
async function getItems() {
    const response = await sdkApiClient.v1.getWalletItems();
    if (response.error) {
        throw new FractalSDKGetItemsUnknownError(`There was an issue fetching items. error = ${response.error.message}`);
    }
    return response.data;
}
async function getItemsForSale({ limit, sortDirection = 'DESCENDING', sortField = 'LIST_TIME', }) {
    const response = await webSdkApiClient.websdk.getForSaleItems({
        limit,
        'sort.direction': sortDirection,
        'sort.field': sortField,
    });
    if (response.error) {
        throw new FractalSDKGetItemsForSaleUnknownError(`There was an issue fetching items for sale. error = ${response.error.message}`);
    }
    return response.data;
}
async function generateBuyTransaction({ quantity, tokenId, walletId, }) {
    const response = await sdkApiClient.v1.tokenBuyTransaction({
        quantity,
        tokenId,
        walletId,
    });
    if (response.error) {
        throw new FractalSDKBuyItemUnknownError(`There was an issue generating the buy transaction. error = ${response.error.message}`);
    }
    return response.data;
}
async function generateListTransaction({ price, quantity = 1, tokenId, walletId, }) {
    const response = await sdkApiClient.v1.tokenSellTransaction({
        price,
        quantity,
        tokenId,
        walletId,
    });
    if (response.error) {
        throw new FractalSDKListItemUnknownError(`There was an issue generating the list transaction. error = ${response.error.message}`);
    }
    return response.data;
}
async function generateCancelListTransaction({ quantity = 1, tokenId, walletId, }) {
    const response = await sdkApiClient.v1.tokenCancelSellTransaction({
        quantity,
        tokenId,
        walletId,
    });
    if (response.error) {
        throw new FractalSDKCancelListItemUnknownError(`There was an issue generating the cancel list transaction. error = ${response.error.message}`);
    }
    return response.data;
}
//# sourceMappingURL=items.js.map